import React, { useState, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';

import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';

import { Stamp, QrCode, Clock, Shield, User, Upload, Download, Save, Eye, CheckCircle, Image, Settings, Copy, RefreshCw } from 'lucide-react';
import QRCode from 'react-qr-code';
import { toast } from 'sonner';

const _API = process.env.REACT_APP_BACKEND_URL;

export default function ModuleSignatureExpert({ getAuthHeader: getAuthHeader, expert }) {
  const [signatureConfig, setSignatureConfig] = useState({
    stampImage: null,
    stampText: '',
    showQRCode: true,
    showTimestamp: true,
    showExpertId: true,
    position: 'bottom-right',
    size: 'medium'
  });
  const [_previewMode, _setPreviewMode] = useState(false);
  const [generatedSignature, setGeneratedSignature] = useState(null);
  const [signatureHistory, setSignatureHistory] = useState([]);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const stampInputRef = useRef(null);
  const _canvasRef = useRef(null);

  // Générer un ID unique de vérification
  const generateVerificationId = () => {
    const timestamp = Date.now().toString(36);
    const random = Math.random().toString(36).substring(2, 8);
    return `EXP-${expert?.id || 'XXX'}-${timestamp}-${random}`.toUpperCase();
  };

  // Générer l'URL de vérification
  const generateVerificationUrl = (verificationId) => {
    return `${window.location.origin}/verification/${verificationId}`;
  };

  // Générer la signature complète
  const generateSignature = () => {
    const verificationId = generateVerificationId();
    const timestamp = new Date().toISOString();
    const verificationUrl = generateVerificationUrl(verificationId);

    const signature = {
      id: verificationId,
      expertId: expert?.id || 'N/A',
      expertName: expert ? `${expert.prenom || ''} ${expert.nom || ''}`.trim() : 'Expert',
      expertEmail: expert?.email || '',
      timestamp: timestamp,
      formattedTimestamp: new Date().toLocaleString('fr-FR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      }),
      verificationUrl: verificationUrl,
      stampImage: signatureConfig.stampImage,
      config: signatureConfig
    };

    setGeneratedSignature(signature);
    
    // Ajouter à l'historique
    setSignatureHistory(prev => [{
      ...signature,
      createdAt: new Date().toISOString()
    }, ...prev.slice(0, 9)]);

    toast.success('Signature générée avec succès');
    return signature;
  };

  // Gérer l'upload du tampon
  const handleStampUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error('Veuillez sélectionner une image');
        return;
      }
      
      const reader = new FileReader();
      reader.onload = (event) => {
        setSignatureConfig({
          ...signatureConfig,
          stampImage: event.target.result
        });
        toast.success('Tampon importé');
      };
      reader.readAsDataURL(file);
    }
  };

  // Copier l'ID de vérification
  const copyVerificationId = () => {
    if (generatedSignature) {
      navigator.clipboard.writeText(generatedSignature.id);
      toast.success('ID de vérification copié');
    }
  };

  // Exporter la signature en image
  const exportSignatureImage = async () => {
    if (!generatedSignature) {
      toast.error('Veuillez d\'abord générer une signature');
      return;
    }

    // Utiliser html2canvas pour capturer le bloc signature
    try {
      const html2canvas = (await import('html2canvas')).default;
      const element = document.getElementById('signature-preview');
      if (element) {
        const canvas = await html2canvas(element, { 
          backgroundColor: '#ffffff',
          scale: 2 
        });
        const link = document.createElement('a');
        link.download = `signature_${generatedSignature.id}.png`;
        link.href = canvas.toDataURL('image/png');
        link.click();
        toast.success('Signature exportée');
      }
    } catch {
      toast.error('Erreur lors de l\'export');
    }
  };

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center">
              <Stamp className="h-6 w-6 text-white" />
            </div>
            Module de Signature Expert
          </h2>
          <p className="text-gray-500 mt-1">
            Générez des signatures authentifiées avec tampon, horodatage et QR code de vérification
          </p>
        </div>
        
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setShowConfigModal(true)}>
            <Settings className="h-4 w-4 mr-2" />
            Configuration
          </Button>
          <Button 
            onClick={generateSignature}
            className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700"
          >
            <Stamp className="h-4 w-4 mr-2" />
            Générer une signature
          </Button>
        </div>
      </div>

      {/* Informations Expert */}
      <Card className="bg-gradient-to-r from-purple-50 to-indigo-50 border-purple-200">
        <CardContent className="py-4">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center text-white text-2xl font-bold">
              {expert?.prenom?.[0] || 'E'}{expert?.nom?.[0] || 'X'}
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                {expert ? `${expert.prenom || ''} ${expert.nom || ''}`.trim() : 'Expert'}
              </h3>
              <p className="text-sm text-gray-600">{expert?.email || 'email@exemple.com'}</p>
              <div className="flex items-center gap-2 mt-1">
                <Badge className="bg-purple-700 text-white font-medium">
                  ID: {expert?.id || 'N/A'}
                </Badge>
                <Badge className="bg-green-700 text-white font-medium">
                  <CheckCircle className="h-3 w-3 mr-1" />
                  Certifié
                </Badge>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Configuration du tampon */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Image className="h-5 w-5 text-purple-600" />
              Configuration du Tampon
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Upload tampon */}
            <div>
              <Label>Image du tampon</Label>
              <input
                ref={stampInputRef}
                type="file"
                accept="image/*"
                onChange={handleStampUpload}
                className="hidden"
              />
              <div 
                onClick={() => stampInputRef.current?.click()}
                className={`mt-2 border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
                  signatureConfig.stampImage 
                    ? 'border-purple-500 bg-purple-50' 
                    : 'border-gray-300 hover:border-gray-400'
                }`}
              >
                {signatureConfig.stampImage ? (
                  <div>
                    <img 
                      src={signatureConfig.stampImage} 
                      alt="Tampon" 
                      className="max-h-32 mx-auto mb-2"
                    />
                    <p className="text-sm text-purple-600">Cliquez pour changer</p>
                  </div>
                ) : (
                  <div>
                    <Upload className="h-10 w-10 mx-auto text-gray-400 mb-2" />
                    <p className="text-sm text-gray-600">Cliquez pour importer votre tampon</p>
                    <p className="text-xs text-gray-400 mt-1">PNG, JPG (transparent recommandé)</p>
                  </div>
                )}
              </div>
            </div>

            {/* Options */}
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <QrCode className="h-5 w-5 text-blue-600" />
                  <span>QR Code de vérification</span>
                </div>
                <input
                  type="checkbox"
                  checked={signatureConfig.showQRCode}
                  onChange={(e) => setSignatureConfig({...signatureConfig, showQRCode: e.target.checked})}
                  className="w-5 h-5 rounded"
                />
              </div>
              
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <Clock className="h-5 w-5 text-green-600" />
                  <span>Horodatage</span>
                </div>
                <input
                  type="checkbox"
                  checked={signatureConfig.showTimestamp}
                  onChange={(e) => setSignatureConfig({...signatureConfig, showTimestamp: e.target.checked})}
                  className="w-5 h-5 rounded"
                />
              </div>
              
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <User className="h-5 w-5 text-purple-600" />
                  <span>ID Expert</span>
                </div>
                <input
                  type="checkbox"
                  checked={signatureConfig.showExpertId}
                  onChange={(e) => setSignatureConfig({...signatureConfig, showExpertId: e.target.checked})}
                  className="w-5 h-5 rounded"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Prévisualisation de la signature */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5 text-green-600" />
              Prévisualisation
            </CardTitle>
          </CardHeader>
          <CardContent>
            {generatedSignature ? (
              <div id="signature-preview" className="border rounded-lg p-6 bg-white">
                {/* Bloc signature */}
                <div className="border-2 border-gray-300 rounded-lg p-4 bg-gradient-to-br from-gray-50 to-white">
                  <div className="flex items-start gap-4">
                    {/* Tampon */}
                    <div className="flex-shrink-0">
                      {signatureConfig.stampImage ? (
                        <img 
                          src={signatureConfig.stampImage} 
                          alt="Tampon Expert" 
                          className="w-24 h-24 object-contain"
                        />
                      ) : (
                        <div className="w-24 h-24 rounded-full border-4 border-purple-600 flex items-center justify-center bg-purple-50">
                          <Stamp className="h-10 w-10 text-purple-600" />
                        </div>
                      )}
                    </div>
                    
                    {/* Informations */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        <Shield className="h-5 w-5 text-green-600" />
                        <span className="font-bold text-green-700">SIGNATURE AUTHENTIFIÉE</span>
                      </div>
                      
                      {signatureConfig.showExpertId && (
                        <div className="text-sm mb-1">
                          <span className="text-gray-500">Expert:</span>{' '}
                          <span className="font-medium">{generatedSignature.expertName}</span>
                        </div>
                      )}
                      
                      {signatureConfig.showExpertId && (
                        <div className="text-sm mb-1">
                          <span className="text-gray-500">ID Expert:</span>{' '}
                          <span className="font-mono font-medium">{generatedSignature.expertId}</span>
                        </div>
                      )}
                      
                      {signatureConfig.showTimestamp && (
                        <div className="text-sm mb-1">
                          <span className="text-gray-500">Date/Heure:</span>{' '}
                          <span className="font-medium">{generatedSignature.formattedTimestamp}</span>
                        </div>
                      )}
                      
                      <div className="text-sm">
                        <span className="text-gray-500">ID Vérification:</span>{' '}
                        <span className="font-mono text-xs bg-gray-100 px-2 py-0.5 rounded">
                          {generatedSignature.id}
                        </span>
                      </div>
                    </div>
                    
                    {/* QR Code */}
                    {signatureConfig.showQRCode && (
                      <div className="flex-shrink-0 text-center">
                        <div className="bg-white p-2 rounded-lg border">
                          <QRCode 
                            value={generatedSignature.verificationUrl} 
                            size={80}
                            level="M"
                          />
                        </div>
                        <p className="text-xs text-gray-500 mt-1">Scanner pour vérifier</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-2 mt-4">
                  <Button size="sm" variant="outline" onClick={copyVerificationId}>
                    <Copy className="h-4 w-4 mr-1" />
                    Copier ID
                  </Button>
                  <Button size="sm" variant="outline" onClick={exportSignatureImage}>
                    <Download className="h-4 w-4 mr-1" />
                    Exporter PNG
                  </Button>
                  <Button size="sm" variant="outline" onClick={generateSignature}>
                    <RefreshCw className="h-4 w-4 mr-1" />
                    Régénérer
                  </Button>
                </div>
              </div>
            ) : (
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-12 text-center">
                <Stamp className="h-16 w-16 mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500 mb-4">Cliquez sur &quot;Générer une signature&quot; pour créer votre bloc signature</p>
                <Button onClick={generateSignature}>
                  Générer maintenant
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Historique des signatures */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-orange-600" />
            Historique des signatures récentes
          </CardTitle>
        </CardHeader>
        <CardContent>
          {signatureHistory.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">ID Vérification</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Date/Heure</th>
                    <th className="px-4 py-3 text-center text-sm font-medium text-gray-700">QR</th>
                    <th className="px-4 py-3 text-center text-sm font-medium text-gray-700">Horodatage</th>
                    <th className="px-4 py-3 text-center text-sm font-medium text-gray-700">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {signatureHistory.map((sig, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <span className="font-mono text-sm bg-gray-100 px-2 py-1 rounded">
                          {sig.id}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm">{sig.formattedTimestamp}</td>
                      <td className="px-4 py-3 text-center">
                        {sig.config.showQRCode ? (
                          <Badge className="bg-green-700 text-white font-semibold">Oui</Badge>
                        ) : (
                          <Badge className="bg-gray-500 text-white font-semibold">Non</Badge>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {sig.config.showTimestamp ? (
                          <Badge className="bg-green-700 text-white font-semibold">Oui</Badge>
                        ) : (
                          <Badge className="bg-gray-500 text-white font-semibold">Non</Badge>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <Button 
                          size="sm" 
                          variant="ghost"
                          onClick={() => {
                            navigator.clipboard.writeText(sig.id);
                            toast.success('ID copié');
                          }}
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <Clock className="h-12 w-12 mx-auto mb-2 opacity-50" />
              <p>Aucune signature générée pour le moment</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal Configuration */}
      <Dialog open={showConfigModal} onOpenChange={setShowConfigModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5 text-purple-600" />
              Configuration avancée
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Texte personnalisé sur le tampon</Label>
              <Input
                value={signatureConfig.stampText}
                onChange={(e) => setSignatureConfig({...signatureConfig, stampText: e.target.value})}
                placeholder="Ex: Expert Agréé"
                className="mt-1"
              />
            </div>
            
            <div>
              <Label>Taille du bloc signature</Label>
              <select
                value={signatureConfig.size}
                onChange={(e) => setSignatureConfig({...signatureConfig, size: e.target.value})}
                className="w-full mt-1 px-3 py-2 border rounded-md"
              >
                <option value="small">Petit</option>
                <option value="medium">Moyen</option>
                <option value="large">Grand</option>
              </select>
            </div>
            
            <div>
              <Label>Position par défaut</Label>
              <select
                value={signatureConfig.position}
                onChange={(e) => setSignatureConfig({...signatureConfig, position: e.target.value})}
                className="w-full mt-1 px-3 py-2 border rounded-md"
              >
                <option value="bottom-right">Bas droite</option>
                <option value="bottom-left">Bas gauche</option>
                <option value="bottom-center">Bas centre</option>
              </select>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfigModal(false)}>
              Fermer
            </Button>
            <Button 
              onClick={() => { setShowConfigModal(false); toast.success('Configuration sauvegardée'); }}
              className="bg-purple-600 hover:bg-purple-700"
            >
              <Save className="h-4 w-4 mr-2" />
              Sauvegarder
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
